import { ApiErrorPresenterService } from 'src/app/core/services/api-error-presenter.service';
import { Signal, inject } from '@angular/core';

import {
  QueryClient,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { NomObjectClass } from 'src/app/core/constants';
import { Alert } from 'src/app/core/models/alert.models';
import { AlertData } from 'src/app/core/services/alert.service';

import { AlertApiService } from '../services/alert-api.service';

/**
 * A feed's alerts: the list read plus the four writes that patch it in place.
 *
 * The sidebar badge count is **not** here. `FeedState.alertsCount` is seeded from
 * `feed.count_alerts` and adjusted by ±1 on the create/delete broadcasts, so it is
 * right before anyone opens the panel - which a list query cannot do. Call sites
 * must therefore keep dispatching `createAlertSuccess` / `deleteAlertSuccess`.
 */

const ALERTS_ROOT_KEY = ['alert', 'list'] as const;

export const feedAlertsQueryKey = (feedId: string | null) =>
  [...ALERTS_ROOT_KEY, { objectId: feedId ?? null }] as const;

/** Every mounted alerts list, whichever object it belongs to. */
const allAlertListsFilter = { queryKey: ALERTS_ROOT_KEY };

export function injectFeedAlertsQuery(feedId: Signal<string | null>) {
  const alertApi = inject(AlertApiService);

  return injectQuery(() => ({
    queryKey: feedAlertsQueryKey(feedId()),
    queryFn: () =>
      firstValueFrom(alertApi.listAlerts(NomObjectClass.FEED, feedId()!)).then(
        (alerts) => alerts ?? [],
      ),
    enabled: !!feedId(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}

export interface CreateAlertInput {
  feedId: string;
  alert: AlertData;
}

export function injectCreateAlertMutation() {
  const alertApi = inject(AlertApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ feedId, alert }: CreateAlertInput) =>
      firstValueFrom(alertApi.createAlert(feedId, alert)),
    onSuccess: (alert, { feedId }) => {
      queryClient.setQueryData<Alert[]>(
        feedAlertsQueryKey(feedId),
        (current) => (current ? [...current, alert] : [alert]),
      );
      toast.showSuccess(
        translate.instant('feed.toasts.alert-created-successfully'),
      );
    },
  }));
}

export interface EditAlertInput {
  alertId: string;
  alert: AlertData;
}

/**
 * Patches every mounted list rather than one feed's: the editor knows the alert
 * id, not necessarily which list is on screen.
 */
export function injectEditAlertMutation() {
  const alertApi = inject(AlertApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({ alertId, alert }: EditAlertInput) =>
      firstValueFrom(alertApi.editAlert(alertId, alert)),
    onSuccess: (saved) => {
      patchAlertLists(queryClient, (alerts) =>
        alerts.map((alert) => (alert.id === saved.id ? saved : alert)),
      );
      toast.showSuccess(
        translate.instant('feed.toasts.alert-edited-successfully'),
      );
    },
  }));
}

/** No toast on either outcome, so a failed delete is silent today. */
export function injectDeleteAlertMutation() {
  const alertApi = inject(AlertApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (alertId: string) =>
      firstValueFrom(alertApi.deleteAlert(alertId)),
    onSuccess: (_result, alertId) => {
      patchAlertLists(queryClient, (alerts) =>
        alerts.filter((alert) => alert.id !== alertId),
      );
    },
  }));
}

/**
 * The mode flip is derived client-side because the endpoint answers with no usable
 * body.
 */
export function injectToggleAlertModeMutation() {
  const alertApi = inject(AlertApiService);
  const presenter = inject(ApiErrorPresenterService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (alertId: string) =>
      firstValueFrom(alertApi.toggleAlertMode(alertId)),
    onSuccess: (_result, alertId) => {
      patchAlertLists(queryClient, (alerts) =>
        alerts.map((alert) =>
          alert.id === alertId
            ? {
                ...alert,
                mode:
                  alert.mode?.id == 1
                    ? { id: 2, title: 'Paused' }
                    : { id: 1, title: 'Running' },
              }
            : alert,
        ),
      );
      toast.showSuccess(
        translate.instant('feed.toasts.alert-mode-changed-successfully'),
      );
    },
    onError: (error: unknown) => {
      presenter.present(error, {
        fallback: translate.instant('feed.toasts.alert-mode-change-failed'),
      });
    },
  }));
}

/**
 * Apply a patch to every cached *feed* alerts list. Delete and toggle are
 * reachable from hosts that carry no feed id (the
 * object-card dropdown), so they cannot target one entry by key.
 */
const patchAlertLists = (
  queryClient: QueryClient,
  patch: (alerts: Alert[]) => Alert[],
): void => {
  queryClient.setQueriesData<Alert[]>(allAlertListsFilter, (current) =>
    current ? patch(current) : current,
  );
};
